import { isNumber, isEqual, sortBy, omit } from 'lodash'

import { discountableOptions, taxesOptions } from 'sharedConstants'

export const convertNumberToPercentage = (number) => {
  if (isNaN(Number(number))) {
    return 0
  }

  return Number((Number(number) * 100).toFixed(4))
}

const convertPercentageToNumber = (percentage = 0) => {
  if (isNaN(Number(percentage))) {
    return 0
  }

  return Number(percentage) / 100
}

// We have one field rate and two radio buttons: taxableRate and taxInclusiveRate
// If taxable is true, then taxableRate is shown and taxInclusiveRate is hidden
// If taxInclusive is true, then taxInclusiveRate is shown and taxableRate is hidden
export const calculateTaxRateInitialValue = (
  taxable,
  taxInclusive,
  taxRate
) => {
  const taxRatePercentage = convertNumberToPercentage(taxRate) || null
  if (taxable) {
    return {
      taxableRate: taxRatePercentage,
      taxInclusiveRate: null,
    }
  }

  if (taxInclusive) {
    return {
      taxInclusiveRate: taxRatePercentage,
      taxableRate: null,
    }
  }
}

// We have one field rate which we send to the backend
// And in UI we have two radio buttons: taxableRate and taxInclusiveRate
// We have to decide which value to send to the backend
// based on the selected radio button
export const calculateTaxRateToSubmit = (
  taxable = false,
  taxableRate,
  taxInclusive = false,
  taxInclusiveRate
) => {
  if (taxable) {
    return convertPercentageToNumber(taxableRate)
  } else if (taxInclusive) {
    return convertPercentageToNumber(taxInclusiveRate)
  } else {
    return null
  }
}

export const transformValuesToSubmitBulkEdit = (
  taxOptions,
  discountableOption,
  values
) => {
  const rate = calculateTaxRateToSubmit(
    values.taxable,
    values.taxableRate,
    values.taxInclusive,
    values.taxInclusiveRate
  )

  const taxValues = () => {
    if (taxOptions === taxesOptions.NON_TAXABLE) {
      return {
        taxable: false,
        taxInclusive: false,
        rate: null,
      }
    } else if (taxOptions === 'clearTaxes' || taxOptions === '') {
      return {}
    } else {
      return {
        taxable: values.taxable,
        taxInclusive: values.taxInclusive,
        rate,
      }
    }
  }

  const discountValues = () => {
    if (discountableOption === discountableOptions.ALL_DISCOUNTABLE) {
      return { discountable: true }
    } else if (
      discountableOption === discountableOptions.DONT_ALL_DISCOUNTABLE
    ) {
      return { discountable: false }
    } else {
      return {}
    }
  }

  const priceValue = () => {
    if (!isNumber(values.price)) {
      return {}
    } else {
      return { price: values.price }
    }
  }

  return {
    ...taxValues(),
    ...discountValues(),
    ...priceValue(),
  }
}
// This function generates item mpappings uuids for deletion
// it is necessary because to delete all assigned modifier groups
// we have to delete particular item mappings
export const transformDataForMappingDeletion = (
  initialModifierItemCategoryUuids,
  modifierItemCategoryUuids,
  modifierGroups,
  itemMappings,
  itemId
) => {
  // get deleted modifier groups uuids
  // this is difference between initialValues and values
  const deletedModifierItemCategoryUuids =
    initialModifierItemCategoryUuids?.filter(
      (modifierItemCategoryUuid) =>
        !modifierItemCategoryUuids.includes(modifierItemCategoryUuid)
    ) ?? []

  // find modifier groups objects with deleted uuids
  // it is necessary to get modifier groups ids
  const deletedModifierItemCategoryUuidsObjects =
    modifierGroups?.filter((modifierGroup) =>
      deletedModifierItemCategoryUuids.includes(modifierGroup.uuid)
    ) ?? []

  // get deleted modifier groups ids
  const deletedModifierItemCategoryIds =
    deletedModifierItemCategoryUuidsObjects?.map(
      (deletedModifierItemCategoryUuidObject) =>
        deletedModifierItemCategoryUuidObject.id
    ) ?? []

  // find item mappings where mappedObjectId is deleted modifier groups ids
  const itemMappingToDelete =
    itemMappings?.filter((itemMapping) =>
      deletedModifierItemCategoryIds.includes(
        itemMapping.mappedObjectId
      )
    ) ?? []

  // remove item mappings where baseItemId is different than item id
  // this is necessary to avoid deleting item mappings from other items
  // baseItemId is id of item that is mapped to modifier group
  const itemMappingToDeleteScopeByBaseItemId =
    itemMappingToDelete?.filter(
      (itemMapping) => itemMapping.baseItemId === itemId
    ) ?? []

  // remove items mappgins where mappedObjectType is not "BaseItemCategory"
  const itemMappingToDeleteScopeByMappedObjectType =
    itemMappingToDeleteScopeByBaseItemId?.filter(
      (itemMapping) =>
        itemMapping.mappedObjectType === 'BaseItemCategory'
    ) ?? []

  // get item mappings uuids
  const itemMappingToDeleteUuids =
    itemMappingToDeleteScopeByMappedObjectType?.map(
      (itemMapping) => itemMapping.uuid
    ) ?? []

  return itemMappingToDeleteUuids
}

export const mapModifierGroupsToItemMappings = (
  modifierGroups,
  itemMappings,
  itemId
) => {
  // get modifier groups ids
  const modifierGroupsIDs =
    modifierGroups?.map((modifierGroup) => modifierGroup.id) ?? []

  // find item mappings where mappedObjectId is modifier groups ids
  const itemMappingsFilterByModifierID =
    itemMappings?.filter((itemMapping) =>
      modifierGroupsIDs.includes(itemMapping.mappedObjectId)
    ) ?? []

  // remove item mappings where baseItemId is different than item id
  const itemMappingsScopedByBaseItemId =
    itemMappingsFilterByModifierID?.filter(
      (itemMapping) => itemMapping.baseItemId === itemId
    ) ?? []

  // remove items mappgins where mappedObjectType is not "BaseItemCategory"
  const itemMappingsMappedToModifierGroups =
    itemMappingsScopedByBaseItemId?.filter(
      (itemMapping) =>
        itemMapping.mappedObjectType === 'BaseItemCategory'
    ) ?? []

  return itemMappingsMappedToModifierGroups
}

export const updateItemMappings = (
  modifierGroups,
  itemMappings,
  itemId
) => {
  const itemMappingsMappedToModifierGroups =
    mapModifierGroupsToItemMappings(
      modifierGroups,
      itemMappings,
      itemId
    )

  const itemMappingsWithMinMax =
    itemMappingsMappedToModifierGroups?.map((itemMapping) => {
      const modifierGroup = modifierGroups?.find(
        (modifierGroup) =>
          modifierGroup.id === itemMapping?.mappedObjectId
      )

      return {
        ...itemMapping,
        minimumCount: modifierGroup?.minimumCount,
        maximumCount: modifierGroup?.maximumCount,
      }
    }) ?? []

  return itemMappingsWithMinMax
}

export const createNewItemMappings = (
  modifierGroups,
  itemMappings,
  itemId
) => {
  // create pairs of mappedObjectId and baseItemId from itemMappings
  const itemMappingsMappedObjectsIDsAndBaseItemIDs =
    itemMappings?.map((itemMapping) => [
      itemMapping?.mappedObjectId,
      itemMapping?.baseItemId,
    ]) ?? []

  // filter those modifier groups that are not mapped to item mappings
  const modifierGroupsWithoutItemMappings = modifierGroups?.filter(
    (modifierGroup) => {
      const isModifierWithourItemMapping =
        !itemMappingsMappedObjectsIDsAndBaseItemIDs?.some(
          (item) =>
            item[0] === modifierGroup?.id && item[1] === itemId
        )

      return isModifierWithourItemMapping
    }
  )

  // create new item mappings
  const itemMappingsToCreate =
    modifierGroupsWithoutItemMappings?.map((modifierGroup) => {
      return {
        mappedObjectId: modifierGroup.id,
        mappedObjectType: 'BaseItemCategory',
        baseItemId: itemId,
        mappingType: 'modifier',
        minimumCount: modifierGroup?.minimumCount ?? null,
        maximumCount: modifierGroup?.maximumCount ?? null,
      }
    }) ?? []

  return itemMappingsToCreate
}

export const calculateItemmMappingsToCreateAndDelete = (
  modifierMinMax,
  modifierGroups,
  itemMappings,
  itemId
) => {
  // get modifier groups uuids
  const updatedModifierGroupsUuids = modifierMinMax.map(
    (modifierMinMax) => modifierMinMax.modifierUuid
  )

  // find modifier groups objects with updated uuids and add min and max count
  const modifierGroupsObjectsWithMinMax = modifierGroups
    .filter((modifierGroup) =>
      updatedModifierGroupsUuids.includes(modifierGroup.uuid)
    )
    .map((modifierGroup) => ({
      ...modifierGroup,
      maximumCount: modifierMinMax.find(
        (modifierMinMax) =>
          modifierMinMax.modifierUuid === modifierGroup.uuid
      ).maximumCount,
      minimumCount: modifierMinMax.find(
        (modifierMinMax) =>
          modifierMinMax.modifierUuid === modifierGroup.uuid
      ).minimumCount,
    }))

  const itemMappingsToUpdate = updateItemMappings(
    modifierGroupsObjectsWithMinMax,
    itemMappings,
    itemId
  )

  const itemMappingsToCreate = createNewItemMappings(
    modifierGroupsObjectsWithMinMax,
    itemMappings,
    itemId
  )

  return {
    itemMappingsToUpdate,
    itemMappingsToCreate,
  }
}

// this function decides if item payload update should contain menuUuids
export const renderItemParamsWithOptionalMenuUuids = (
  initialMenuUuids,
  itemParams
) => {
  const result = itemParams.map((itemParam) => {
    if (
      isEqual(
        // we have to sort arrays because menuUuids can be in different order
        // and isEqual will return false
        sortBy(itemParam.menuUuids),
        sortBy(initialMenuUuids)
      )
    ) {
      return omit(itemParam, 'menuUuids')
    } else {
      return itemParam
    }
  })

  return result
}
